import { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import styles from '../../Styles/ComponentStyles/PendingRequests/RejectPendingRequestPopup.module.scss';

export interface RejectPendingRequestConfirmPayload {
    rejection_note: string;
    notify_agent_on_rejection: boolean;
}

interface RejectPendingRequestPopupContentProps {
    open: boolean;
    agentDisplayName: string;
    onCancel: () => void;
    onConfirm: (payload: RejectPendingRequestConfirmPayload) => void;
    isSubmitting?: boolean;
}

const WarningTriangleIcon = () => (
    <svg className={styles.warningIcon} viewBox="0 0 56 56" fill="none" aria-hidden>
        <path d="M28 8L9 46h38L28 8z" fill="#DC2626" stroke="#B91C1C" strokeWidth="1" strokeLinejoin="round" />
        <path d="M28 22v12" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        <circle cx="28" cy="40" r="2" fill="#fff" />
    </svg>
);

const RejectPendingRequestPopupContent = ({
    open,
    agentDisplayName,
    onCancel,
    onConfirm,
    isSubmitting = false,
}: RejectPendingRequestPopupContentProps) => {
    const [reason, setReason] = useState('');
    const [sendRejectionEmail, setSendRejectionEmail] = useState(false);

    useEffect(() => {
        if (open) {
            setReason('');
            setSendRejectionEmail(false);
        }
    }, [open, agentDisplayName]);

    return (
        <Grid item xs={12}>
            <Grid container spacing={0}>
                {/* Warning Icon */}
                <Grid item xs={12}>
                    <Box className={styles.warningWrap}>
                        <WarningTriangleIcon />
                    </Box>
                </Grid>

                {/* Confirmation Text */}
                <Grid item xs={12}>
                    <Typography component="p" className={styles.confirmText}>
                        Are you sure you want to reject {agentDisplayName}&apos;s request?
                    </Typography>
                </Grid>

                {/* Form Block */}
                <Grid container item xs={12} className={styles.formBlock}>
                    <Grid item xs={12}>
                        <label htmlFor="reject-pending-reason" className={styles.reasonLabel}>
                            Reason
                        </label>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            id="reject-pending-reason"
                            className={styles.reasonField}
                            placeholder="Lorem ipsum dolor sit amet consectetur."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                            variant="outlined"
                            disabled={isSubmitting}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            className={styles.checkboxRow}
                            control={
                                <Checkbox
                                    checked={sendRejectionEmail}
                                    onChange={(_, checked) => setSendRejectionEmail(checked)}
                                    disabled={isSubmitting}
                                    size="small"
                                    sx={{ color: '#64748B', '&.Mui-checked': { color: '#3B78CE' } }}
                                />
                            }
                            label="Send rejection email to the agent"
                        />
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Grid container item xs={12} className={styles.actions}>
                    <Grid item>
                        <Button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button
                            type="button"
                            className={styles.rejectBtn}
                            onClick={() =>
                                onConfirm({
                                    rejection_note: reason.trim(),
                                    notify_agent_on_rejection: sendRejectionEmail,
                                })
                            }
                            disabled={isSubmitting}
                        >
                            Reject
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default RejectPendingRequestPopupContent;
